/**
 * Centralized API route definitions
 * This ensures consistency across the frontend and can be shared in a monorepo
 */
export const API_ROUTES = {
    // Auth endpoints
    AUTH: {
        LOGIN: '/api/auth/login',
        LOGOUT: '/api/auth/logout',
        CALLBACK: '/api/auth/callback',
        SESSION: '/api/auth/session',
        SIGN_OUT: '/api/auth/sign-out',
        WIDGET_TOKEN: '/api/auth/widget-token',
        SWITCH_ORGANIZATION: '/api/auth/switch-organization',
        USER_CONTEXT: '/api/auth/user-context',
        HEALTH: '/api/auth/health',
    },
    // Agent endpoints (Company User)
    AGENTS: {
        GET: '/api/agents',
        CREATE: '/api/agents',
        UPDATE: '/api/agents/:id',
        DELETE: '/api/agents/:id',
    },
    // Company endpoints (Company User)
    COMPANIES: {
        GET: '/api/companies',
        UPDATE: '/api/companies/:id',
        LISTINGS_FALLBACK_LINK: '/api/companies/:id/listings-fallback-link',
        PHONE_AVAILABLE: '/api/companies/:id/phone/available',
        PHONE_PROVISION: '/api/companies/:id/phone/provision',
    },
    // Onboarding endpoints (Company User) — flag-gated by self-service-onboarding
    ONBOARDING: {
        GET: '/api/onboarding',
        UPDATE: '/api/onboarding',
        EVENTS: '/api/onboarding/events',
        RECAP: '/api/onboarding/recap',
        PERSONALIZATION: '/api/onboarding/personalization',
    },
    // Billing endpoints — flag-gated by self-service-onboarding
    BILLING: {
        GET_CONFIG: '/api/billing/config',
        CREATE_SETUP_INTENT: '/api/billing/setup-intent',
        CREATE_SUBSCRIPTION: '/api/billing/subscription',
        GET_SUBSCRIPTION: '/api/billing/subscription',
        UPDATE_PAYMENT_METHOD: '/api/billing/payment-method',
        CANCEL_SUBSCRIPTION: '/api/billing/subscription',
        REACTIVATE_SUBSCRIPTION: '/api/billing/subscription/reactivate',
        LIST_INVOICES: '/api/billing/invoices',
        GET_USAGE: '/api/billing/usage',
        STRIPE_WEBHOOK: '/api/billing/stripe/webhook',
    },
    // Reporting endpoints
    REPORTING: {
        CALL: '/api/reporting/call',
        CALL_INDIVIDUAL: '/api/reporting/call/individual',
        SMS: '/api/reporting/sms',
        ACTIVITY: '/api/reporting/activity',
        ACTIONS: '/api/reporting/actions',
    },
    // Feature Flags endpoints
    FEATURE_FLAGS: {
        GET: '/api/feature-flags',
    },
    // Node-graph demo agents (Company User) — standalone rows, flag-gated UI
    NODE_AGENTS: {
        GET_ALL: '/api/node-agents',
        GET: '/api/node-agents/:id',
        CREATE: '/api/node-agents',
        UPDATE: '/api/node-agents/:id',
        DELETE: '/api/node-agents/:id',
        DISPATCH: '/api/node-agents/:id/dispatch',
    },
    // Graphs (Company User): the node model's agent, stored as rows
    GRAPHS: {
        GET_ALL: '/api/graphs',
        GET: '/api/graphs/:id',
        ASSEMBLED: '/api/graphs/:id/assembled',
        CREATE: '/api/graphs',
        UPDATE: '/api/graphs/:id',
        DELETE: '/api/graphs/:id',
        DISPATCH: '/api/graphs/:id/dispatch',
    },
    // Triggers (Company User) - selectable catalog for agent setup + custom
    // trigger CRUD scoped to the caller's company
    TRIGGERS: {
        GET_ALL: '/api/triggers',
        GET_DEFINITIONS: '/api/triggers/definitions',
        CREATE: '/api/triggers',
        UPDATE: '/api/triggers/:id',
        DELETE: '/api/triggers/:id',
    },
    // Public API observability (Company User) — own-company usage, webhook
    // subscriptions and deliveries
    PUBLIC_API_OBSERVABILITY: {
        USAGE: '/api/public-api/usage',
        INVOCATIONS: '/api/public-api/invocations',
        INVOCATION: '/api/public-api/invocations/:id',
        WEBHOOK_SUBSCRIPTIONS: '/api/public-api/webhook-subscriptions',
        WEBHOOK_DELIVERIES: '/api/public-api/webhook-deliveries',
        WEBHOOK_DELIVERY: '/api/public-api/webhook-deliveries/:id',
    },
    // Knowledge Base endpoints (Company User)
    KNOWLEDGE_BASES: {
        GET_ALL: '/api/knowledge-bases',
        GET: '/api/knowledge-bases/:id',
        CREATE: '/api/knowledge-bases',
        UPDATE: '/api/knowledge-bases/:id',
        DELETE: '/api/knowledge-bases/:id',
        LINKS: {
            GET_ALL: '/api/knowledge-bases/links',
            GET: '/api/knowledge-bases/links/:id',
            UPDATE: '/api/knowledge-bases/links/:id',
        },
        PRONUNCIATIONS: {
            GET_ALL: '/api/knowledge-bases/pronunciations',
            GET: '/api/knowledge-bases/pronunciations/:id',
            CREATE: '/api/knowledge-bases/pronunciations',
            UPDATE: '/api/knowledge-bases/pronunciations/:id',
            DELETE: '/api/knowledge-bases/pronunciations/:id',
        },
    },
    // Admin endpoints (Super Admin)
    ADMIN: {
        // Agent endpoints
        AGENTS: {
            GET: '/api/admin/agents',
            CREATE: '/api/admin/agents',
            UPDATE: '/api/admin/agents/:id',
            DELETE: '/api/admin/agents/:id',
        },
        // Node-graph agent endpoints (Super Admin)
        NODE_AGENTS: {
            GET_ALL: '/api/admin/node-agents',
            GET: '/api/admin/node-agents/:id',
            CREATE: '/api/admin/node-agents',
            UPDATE: '/api/admin/node-agents/:id',
            DELETE: '/api/admin/node-agents/:id',
            DISPATCH: '/api/admin/node-agents/:id/dispatch',
        },
        // Graph endpoints (Super Admin)
        GRAPHS: {
            GET_ALL: '/api/admin/graphs',
            GET: '/api/admin/graphs/:id',
            CREATE: '/api/admin/graphs',
            UPDATE: '/api/admin/graphs/:id',
            DELETE: '/api/admin/graphs/:id',
            DISPATCH: '/api/admin/graphs/:id/dispatch',
        },
        // API Keys endpoints (Super Admin)
        API_KEYS: {
            CREATE: '/api/admin/api-keys',
            LIST: '/api/admin/api-keys',
            UPDATE: '/api/admin/api-keys/:id',
            DELETE: '/api/admin/api-keys/:id',
            GRANTABLE_SCOPES: '/api/admin/api-keys/grantable-scopes',
        },
        // Public API observability endpoints (Super Admin)
        PUBLIC_API: {
            USAGE: '/api/admin/public-api/usage',
            INVOCATIONS: '/api/admin/public-api/invocations',
            INVOCATION: '/api/admin/public-api/invocations/:id',
            WEBHOOK_SUBSCRIPTIONS: '/api/admin/public-api/webhook-subscriptions',
            WEBHOOK_DELIVERIES: '/api/admin/public-api/webhook-deliveries',
            WEBHOOK_DELIVERY: '/api/admin/public-api/webhook-deliveries/:id',
        },
        // Contact endpoints (Super Admin)
        CONTACTS: {
            GET_BY_COMPANY: '/api/admin/contacts/company/:companyId',
        },
        // Company endpoints
        COMPANIES: {
            SEARCH: '/api/admin/companies',
            GET_BY_ID: '/api/admin/companies/:id',
            UPDATE: '/api/admin/companies/:id',
            LISTINGS_FALLBACK_LINK: '/api/admin/companies/:id/listings-fallback-link',
        },
        // Knowledge Base endpoints (Super Admin)
        KNOWLEDGE_BASES: {
            GET_ALL: '/api/admin/knowledge-bases',
            GET: '/api/admin/knowledge-bases/:id',
            CREATE: '/api/admin/knowledge-bases',
            UPDATE: '/api/admin/knowledge-bases/:id',
            DELETE: '/api/admin/knowledge-bases/:id',
            LINKS: {
                GET_ALL: '/api/admin/knowledge-bases/links',
                GET: '/api/admin/knowledge-bases/links/:id',
                UPDATE: '/api/admin/knowledge-bases/links/:id',
            },
            PRONUNCIATIONS: {
                GET_ALL: '/api/admin/knowledge-bases/pronunciations',
                GET: '/api/admin/knowledge-bases/pronunciations/:id',
                CREATE: '/api/admin/knowledge-bases/pronunciations',
                UPDATE: '/api/admin/knowledge-bases/pronunciations/:id',
                DELETE: '/api/admin/knowledge-bases/pronunciations/:id',
            },
        },
        // Directory endpoints (Super Admin)
        DIRECTORY: {
            STAFF: '/api/admin/directory/staff',
            STAFF_BY_ID: '/api/admin/directory/staff/:id',
            STAFF_DELETE_PREVIEW: '/api/admin/directory/staff/:id/delete-preview',
            ROLES: '/api/admin/directory/roles',
            ROLES_BY_ID: '/api/admin/directory/roles/:id',
            ROLES_DELETE_PREVIEW: '/api/admin/directory/roles/:id/delete-preview',
            ROLE_MEMBERS: '/api/admin/directory/roles/:id/members',
            ROLE_MEMBER_BY_ID: '/api/admin/directory/roles/:id/members/:staffId',
            ROLE_MEMBERS_ORDER: '/api/admin/directory/roles/:id/members/order',
            ROLE_RESOLVE: '/api/admin/directory/roles/:id/resolve',
            ROLE_ROTATION: '/api/admin/directory/roles/:id/rotation',
            PROPERTY_GROUPS: '/api/admin/directory/property-groups',
            PROPERTY_GROUPS_BY_ID: '/api/admin/directory/property-groups/:id',
            PROPERTY_GROUPS_DELETE_PREVIEW: '/api/admin/directory/property-groups/:id/delete-preview',
            PROPERTY_GROUP_MEMBERS: '/api/admin/directory/property-groups/:id/members',
            PROPERTY_GROUP_MEMBER_BY_ID: '/api/admin/directory/property-groups/:id/members/:propertyId',
            ACTION_GROUP_ASSIGNEE_PREVIEW: '/api/admin/directory/action-groups/:actionGroupId/assignee-preview',
            SCHEDULE: '/api/admin/directory/schedule',
            SCHEDULE_BY_ID: '/api/admin/directory/schedule/:id',
        },
        // Property endpoints (Super Admin)
        PROPERTIES: {
            GET_BY_COMPANY: '/api/admin/properties',
        },
        // Reporting endpoints (Super Admin)
        REPORTING: {
            CALL: '/api/admin/reporting/call',
            CALL_INDIVIDUAL: '/api/admin/reporting/call/individual',
            SMS: '/api/admin/reporting/sms',
            ACTIVITY: '/api/admin/reporting/activity',
            ACTIONS: '/api/admin/reporting/actions',
            PERFORMANCE: '/api/admin/reporting/performance',
            PERFORMANCE_SESSIONS: '/api/admin/reporting/performance/sessions',
            PERFORMANCE_SESSION_TURNS: '/api/admin/reporting/performance/sessions/:conferenceId/turns',
        },
        // Triggers (Super Admin) - selectable catalog + standalone trigger CRUD by companyId
        TRIGGERS: {
            GET_ALL: '/api/admin/triggers',
            GET_DEFINITIONS: '/api/admin/triggers/definitions',
            CREATE: '/api/admin/triggers',
            UPDATE: '/api/admin/triggers/:id',
            DELETE: '/api/admin/triggers/:id',
        },
        // Scenario endpoints (Super Admin)
        SCENARIOS: {
            GET_BY_COMPANY: '/api/admin/scenarios',
            GET: '/api/admin/scenarios/:id',
            CREATE: '/api/admin/scenarios',
            UPDATE: '/api/admin/scenarios/:id',
            DELETE: '/api/admin/scenarios/:id',
        },
        // Catalog actions — individual WorkflowAction rows (Super Admin)
        ACTIONS: {
            GET_BY_COMPANY: '/api/admin/actions',
            GET: '/api/admin/actions/:id',
            GET_BY_IDS: '/api/admin/actions/by-ids',
            GET_USAGE: '/api/admin/actions/:id/usage',
            GET_USAGE_COUNTS: '/api/admin/actions/usage-counts',
            CREATE: '/api/admin/actions',
            BULK_SAVE: '/api/admin/actions/bulk',
            UPDATE: '/api/admin/actions/:id',
            DELETE: '/api/admin/actions/:id',
        },
        // Action groups — NodeActionGroup rows (Super Admin, companyId via ?companyId=)
        ACTION_GROUPS: {
            GET_BY_COMPANY: '/api/admin/action-groups',
            GET: '/api/admin/action-groups/:id',
            GET_USAGE: '/api/admin/action-groups/:id/usage',
            GET_USAGE_COUNTS: '/api/admin/action-groups/usage-counts',
            CREATE: '/api/admin/action-groups',
            UPDATE: '/api/admin/action-groups/:id',
            DELETE: '/api/admin/action-groups/:id',
        },
        // Workflow endpoints (Super Admin)
        WORKFLOWS: {
            GET_BY_COMPANY: '/api/admin/workflows',
            GET_HANDOFF_BY_COMPANY: '/api/admin/workflows/handoff',
            GET: '/api/admin/workflows/:id',
            CREATE_HANDOFF: '/api/admin/workflows/handoff',
            DUPLICATE_HANDOFF: '/api/admin/workflows/handoff/:id/duplicate',
            UPDATE: '/api/admin/workflows/:id',
            UPDATE_HANDOFF: '/api/admin/workflows/handoff/:id',
            DELETE: '/api/admin/workflows/:id',
            ENABLE: '/api/admin/workflows/:id/enable',
            DISABLE: '/api/admin/workflows/:id/disable',
        },
        // Conference endpoints (Super Admin)
        CONFERENCES: {
            GET_SIGNED_RECORDING_URL: '/api/admin/conferences/:id/signed-recording-url',
        },
        // Threads endpoints (Super Admin)
        THREADS: {
            GET_ALL: '/api/admin/threads',
            GET_BY_CONTACT: '/api/admin/threads/contact/:contactId',
            GET_BY_ID: '/api/admin/threads/:id',
            GET_AROUND_THREAD: '/api/admin/threads/around/:threadId',
        },
        // Thread Events endpoints (Super Admin)
        THREAD_EVENTS: {
            GET_BY_THREAD: '/api/admin/thread-events/thread/:threadId',
            GET_BEFORE_CURSOR: '/api/admin/thread-events/thread/:threadId/before/:cursor',
            GET_AROUND_MESSAGE: '/api/admin/thread-events/thread/:threadId/around/:eventId',
            GET_AROUND_SESSION: '/api/admin/thread-events/thread/:threadId/around-session/:sessionId',
            GET_BY_CONTACT: '/api/admin/thread-events/contact/:companyId/:contactId',
            GET_BY_ID: '/api/admin/thread-events/:id',
            GET_BY_SOURCE: '/api/admin/thread-events/source/:sourceEventId',
            GET_BY_CORRELATION: '/api/admin/thread-events/correlation/:correlationId',
            SEARCH: '/api/admin/thread-events/search',
            CREATE: '/api/admin/thread-events',
            UPDATE_VISIBILITY: '/api/admin/thread-events/:id/visibility',
        },
        // Listings endpoints (Super Admin)
        LISTINGS: {
            GET_ALL: '/api/admin/listings',
            SCRAPE: '/api/admin/listings/scrape',
            SYNC_URL: '/api/admin/listings/sync-url',
            SYNC_URL_STATUS: '/api/admin/listings/sync-url/status',
            CSV_UPLOAD: '/api/admin/listings/csv-upload',
        },
        // Channel Settings endpoints (Super Admin)
        CHANNEL_SETTINGS: {
            GET_ALL: '/api/admin/channel-settings',
            GET: '/api/admin/channel-settings/:channel',
            UPDATE: '/api/admin/channel-settings/:channel',
        },
        // Integrations endpoints (Super Admin)
        INTEGRATIONS: {
            GET: '/api/admin/integrations',
            STATUS: '/api/admin/integrations/status',
            SYNC: '/api/admin/integrations/:integrationId/sync',
            SYNC_LISTINGS: '/api/admin/integrations/:integrationType/sync-listings',
            START_LISTING_SYNC: '/api/admin/integrations/:integrationType/listing-sync/start',
            STOP_LISTING_SYNC: '/api/admin/integrations/:integrationType/listing-sync/stop',
            SYNC_CONFIG: '/api/admin/integrations/:integrationType/sync-config',
            ACCOUNT_CREDENTIALS: {
                CREATE: '/api/admin/integrations/:integrationType/account-credentials',
                UPDATE: '/api/admin/integrations/:integrationType/account-credentials',
                DELETE: '/api/admin/integrations/:integrationType/account-credentials',
            },
            API_KEY_CREDENTIALS: {
                CREATE: '/api/admin/integrations/:integrationType/api-key-credentials',
                UPDATE: '/api/admin/integrations/:integrationType/api-key-credentials',
                DELETE: '/api/admin/integrations/:integrationType/api-key-credentials',
            },
            METADATA_ONLY: {
                CREATE: '/api/admin/integrations/:integrationType/metadata',
                UPDATE: '/api/admin/integrations/:integrationType/metadata',
                DELETE: '/api/admin/integrations/:integrationType/metadata',
            },
            BUILDIUM: {
                TASK_CATEGORIES: '/api/admin/integrations/buildium/task-categories',
                STAFF_USERS: '/api/admin/integrations/buildium/staff-users',
                VENDORS: '/api/admin/integrations/buildium/vendors',
            },
            HUBSPOT: {
                INSTALL: '/api/admin/integrations/hubspot/install',
                ASSIGNEES: '/api/admin/integrations/hubspot/assignees',
                TICKET_PIPELINES: '/api/admin/integrations/hubspot/ticket-pipelines',
            },
            RENT_MANAGER: {
                STATUSES: '/api/admin/integrations/rent-manager/statuses',
                LOCATIONS: '/api/admin/integrations/rent-manager/locations',
            },
            RENTVINE: {
                PROPERTIES: '/api/admin/integrations/rentvine/properties',
            },
            DOORLOOP: {
                CONNECT: '/api/admin/integrations/doorloop/connect',
                USERS: '/api/admin/integrations/doorloop/users',
                VENDORS: '/api/admin/integrations/doorloop/vendors',
            },
        },
        // Partner Management endpoints (Super Admin)
        PARTNERS: {
            LIST: '/api/admin/partners',
            CREATE: '/api/admin/partners',
            GET: '/api/admin/partners/:id',
            UPDATE_STATUS: '/api/admin/partners/:id/status',
            UPDATE_SIP_TRUNK: '/api/admin/partners/:id/sip-trunk',
            GENERATE_CREDENTIALS: '/api/admin/partners/:id/credentials',
            GET_CREDENTIALS: '/api/admin/partners/:id/credentials',
            GET_COMPANIES: '/api/admin/partners/:id/companies',
            GET_PARTNER_AUDIT: '/api/admin/partners/:id/audit',
        },
        // LLM Evaluation endpoints (Super Admin)
        EVAL: {
            CASES: '/api/admin/eval/cases',
            CASE_BY_ID: '/api/admin/eval/cases/:id',
            RUNS: '/api/admin/eval/runs',
            RUN_BY_ID: '/api/admin/eval/runs/:id',
            RUN_ESTIMATE: '/api/admin/eval/runs/estimate',
            RESULTS: '/api/admin/eval/results',
            RESULT_BY_ID: '/api/admin/eval/results/:id',
        },
        // LM Run Log endpoints (Super Admin)
        LM_RUN_LOGS: {
            LIST: '/api/admin/lm-run-logs',
            BY_ID: '/api/admin/lm-run-logs/:id',
        },
        // Decision Log endpoints (Super Admin)
        DECISION_LOGS: {
            LIST: '/api/admin/decision-logs',
            BY_ID: '/api/admin/decision-logs/:id',
        },
        // Scratchpad diagnostics (Super Admin)
        SCRATCHPAD: {
            BY_SESSION: '/api/admin/scratchpad/sessions/:sessionId',
        },
    },
    // Partner Dashboard endpoints (Partner Admins) — versioned under /v1/
    PARTNER: {
        CREDENTIALS: '/api/v1/partner/credentials',
        COMPANIES: '/api/v1/partner/companies',
    },
    // Partner Provisioning API (M2M) — versioned under /v1/
    PROVISIONING: {
        UPDATE_COMPANY: '/api/v1/provisioning/companies/:companyId',
    },
    // Property endpoints (Company User)
    PROPERTIES: {
        GET: '/api/properties',
    },
    // Property Resolution endpoints
    PROPERTY_RESOLUTION: {
        EXECUTE: '/api/property-resolution/execute',
        VALIDATE: '/api/property-resolution/validate',
    },
    // Unit Resolution endpoints
    UNIT_RESOLUTION: {
        EXECUTE: '/api/unit-resolution/execute',
        VALIDATE: '/api/unit-resolution/validate',
    },
    // Contact Resolution endpoints
    CONTACT_RESOLUTION: {
        EXECUTE: '/api/contact-resolution/execute',
        VALIDATE: '/api/contact-resolution/validate',
    },
    // Completions endpoints
    COMPLETIONS: {
        WORKFLOW_HANDOFF: '/api/completions/workflow/handoff',
    },
    // Scenario endpoints (Company User)
    SCENARIOS: {
        GET_ALL: '/api/scenarios',
        GET: '/api/scenarios/:id',
        CREATE: '/api/scenarios',
        UPDATE: '/api/scenarios/:id',
        DELETE: '/api/scenarios/:id',
    },
    // Catalog actions — individual WorkflowAction rows (Company User)
    ACTIONS: {
        GET_ALL: '/api/actions',
        GET: '/api/actions/:id',
        GET_BY_IDS: '/api/actions/by-ids',
        GET_USAGE: '/api/actions/:id/usage',
        GET_USAGE_COUNTS: '/api/actions/usage-counts',
        CREATE: '/api/actions',
        BULK_SAVE: '/api/actions/bulk',
        UPDATE: '/api/actions/:id',
        DELETE: '/api/actions/:id',
    },
    // Action groups — NodeActionGroup rows (Company User)
    ACTION_GROUPS: {
        GET_ALL: '/api/action-groups',
        GET: '/api/action-groups/:id',
        GET_USAGE: '/api/action-groups/:id/usage',
        GET_USAGE_COUNTS: '/api/action-groups/usage-counts',
        CREATE: '/api/action-groups',
        UPDATE: '/api/action-groups/:id',
        DELETE: '/api/action-groups/:id',
    },
    // Workflow endpoints (Company User)
    WORKFLOWS: {
        GET_ALL: '/api/workflows',
        GET_HANDOFF: '/api/workflows/handoff',
        GET: '/api/workflows/:id',
        CREATE_HANDOFF: '/api/workflows/handoff',
        DUPLICATE_HANDOFF: '/api/workflows/handoff/:id/duplicate',
        UPDATE: '/api/workflows/:id',
        UPDATE_HANDOFF: '/api/workflows/handoff/:id',
        DELETE: '/api/workflows/:id',
        ENABLE: '/api/workflows/:id/enable',
        DISABLE: '/api/workflows/:id/disable',
    },
    // Directory endpoints (Company User)
    DIRECTORY: {
        STAFF: '/api/directory/staff',
        STAFF_BY_ID: '/api/directory/staff/:id',
        STAFF_DELETE_PREVIEW: '/api/directory/staff/:id/delete-preview',
        ROLES: '/api/directory/roles',
        ROLES_BY_ID: '/api/directory/roles/:id',
        ROLES_DELETE_PREVIEW: '/api/directory/roles/:id/delete-preview',
        ROLE_MEMBERS: '/api/directory/roles/:id/members',
        ROLE_MEMBER_BY_ID: '/api/directory/roles/:id/members/:staffId',
        ROLE_MEMBERS_ORDER: '/api/directory/roles/:id/members/order',
        ROLE_RESOLVE: '/api/directory/roles/:id/resolve',
        ROLE_ROTATION: '/api/directory/roles/:id/rotation',
        PROPERTY_GROUPS: '/api/directory/property-groups',
        PROPERTY_GROUPS_BY_ID: '/api/directory/property-groups/:id',
        PROPERTY_GROUPS_DELETE_PREVIEW: '/api/directory/property-groups/:id/delete-preview',
        PROPERTY_GROUP_MEMBERS: '/api/directory/property-groups/:id/members',
        PROPERTY_GROUP_MEMBER_BY_ID: '/api/directory/property-groups/:id/members/:propertyId',
        ACTION_GROUP_CONTACTS: '/api/directory/action-groups/:actionGroupId/contacts',
        ACTION_GROUP_LINK_ROLE: '/api/directory/action-groups/:actionGroupId/contacts/role',
        ACTION_GROUP_LINK_STAFF: '/api/directory/action-groups/:actionGroupId/contacts/staff',
        ACTION_GROUP_ASSIGNEE_PREVIEW: '/api/directory/action-groups/:actionGroupId/assignee-preview',
        ACTION_CONTACT_BY_ID: '/api/directory/action-contacts/:id',
        SCHEDULE: '/api/directory/schedule',
        SCHEDULE_BY_ID: '/api/directory/schedule/:id',
    },
    // Contacts endpoints (Company User)
    CONTACTS: {
        LIST: '/api/contacts',
    },
    // Threads endpoints (Company Users)
    THREADS: {
        GET_ALL: '/api/threads',
        GET_BY_CONTACT: '/api/threads/contact/:contactId',
        GET_BY_ID: '/api/threads/:id',
        GET_AROUND_THREAD: '/api/threads/around/:threadId',
    },
    // Thread Events endpoints (Company Users)
    THREAD_EVENTS: {
        GET_BY_THREAD: '/api/thread-events/thread/:threadId',
        GET_BEFORE_CURSOR: '/api/thread-events/thread/:threadId/before/:cursor',
        GET_AROUND_MESSAGE: '/api/thread-events/thread/:threadId/around/:eventId',
        GET_AROUND_SESSION: '/api/thread-events/thread/:threadId/around-session/:sessionId',
        GET_BY_CONTACT: '/api/thread-events/contact/:companyId/:contactId',
        GET_BY_ID: '/api/thread-events/:id',
        GET_BY_SOURCE: '/api/thread-events/source/:sourceEventId',
        GET_BY_CORRELATION: '/api/thread-events/correlation/:correlationId',
        SEARCH: '/api/thread-events/search',
        CREATE: '/api/thread-events',
        UPDATE_VISIBILITY: '/api/thread-events/:id/visibility',
    },
    // Listings endpoints (Company User)
    LISTINGS: {
        GET_ALL: '/api/listings',
        SCRAPE: '/api/listings/scrape',
        SYNC_URL: '/api/listings/sync-url',
        SYNC_URL_STATUS: '/api/listings/sync-url/status',
        CSV_UPLOAD: '/api/listings/csv-upload',
    },
    // Channel Settings endpoints (Company User)
    CHANNEL_SETTINGS: {
        GET_ALL: '/api/channel-settings',
        GET: '/api/channel-settings/:channel',
        UPDATE: '/api/channel-settings/:channel',
    },
    // Integrations endpoints (Company User)
    INTEGRATIONS: {
        GET: '/api/integrations',
        STATUS: '/api/integrations/status',
        SYNC: '/api/integrations/:integrationId/sync',
        SYNC_LISTINGS: '/api/integrations/:integrationType/sync-listings',
        START_LISTING_SYNC: '/api/integrations/:integrationType/listing-sync/start',
        STOP_LISTING_SYNC: '/api/integrations/:integrationType/listing-sync/stop',
        SYNC_CONFIG: '/api/integrations/:integrationType/sync-config',
        BUILDIUM: {
            INSTALL: '/api/integrations/buildium/install',
            CALLBACK: '/api/integrations/buildium/callback',
            TASK_CATEGORIES: '/api/integrations/buildium/task-categories',
            STAFF_USERS: '/api/integrations/buildium/staff-users',
            VENDORS: '/api/integrations/buildium/vendors',
        },
        HUBSPOT: {
            INSTALL: '/api/integrations/hubspot/install',
            CALLBACK: '/api/integrations/hubspot/callback',
            ASSIGNEES: '/api/integrations/hubspot/assignees',
            TICKET_PIPELINES: '/api/integrations/hubspot/ticket-pipelines',
        },
        SHOWMOJO: {
            DISCONNECT: '/api/integrations/showmojo/disconnect',
        },
        ACCOUNT_CREDENTIALS: {
            CREATE: '/api/integrations/:integrationType/account-credentials',
            UPDATE: '/api/integrations/:integrationType/account-credentials',
            DELETE: '/api/integrations/:integrationType/account-credentials',
        },
        API_KEY_CREDENTIALS: {
            CREATE: '/api/integrations/:integrationType/api-key-credentials',
            UPDATE: '/api/integrations/:integrationType/api-key-credentials',
            DELETE: '/api/integrations/:integrationType/api-key-credentials',
        },
        RENT_MANAGER: {
            STATUSES: '/api/integrations/rent-manager/statuses',
        },
        RENTVINE: {
            PROPERTIES: '/api/integrations/rentvine/properties',
        },
        DOORLOOP: {
            CONNECT: '/api/integrations/doorloop/connect',
            STATUS: '/api/integrations/doorloop/status',
            DISCONNECT: '/api/integrations/doorloop',
            USERS: '/api/integrations/doorloop/users',
            VENDORS: '/api/integrations/doorloop/vendors',
        },
        METADATA_ONLY: {
            UPDATE: '/api/integrations/:integrationType/metadata',
            DELETE: '/api/integrations/:integrationType/metadata',
        },
    },
    // Conference endpoints
    CONFERENCES: {
        GET_SIGNED_RECORDING_URL: '/api/conferences/:id/signed-recording-url',
    },
    // Health endpoints
    HEALTH: {
        API: '/api/health',
    },
    // LiveKit endpoints
    LIVEKIT: {
        DISPATCH: '/api/livekit/dispatch',
        WEB_DISPATCH: '/api/livekit/web-dispatch',
    },
    // SMS endpoints
    SMS: {
        STATUS_CALLBACK: '/api/sms/status-callback',
    },
    // Voice endpoints
    VOICE: {
        INITIATE: '/api/voice/initiate',
        CONFERENCE_STATUS: '/api/voice/conference/status',
        RECORDING_STATUS: '/api/voice/recording/status',
        UPDATE_CONFERENCE: '/api/voice/conference/:callSid',
        STAFF_CALL_STATUS: '/api/voice/staff-call/status',
        STAFF_CONFERENCE_STATUS: '/api/voice/staff-conference/status',
        CREATE_OUTBOUND_CONFERENCE: '/api/voice/conferences/outbound',
        UPDATE_OUTBOUND_CALL_STATUS: '/api/voice/conferences/outbound/:callId/status',
        GET_SIGNED_URL: '/api/voice/conferences/outbound/recordings/signed-url',
        SCHEDULE_OUTBOUND_CALL: '/api/voice/outbound/schedule',
    },
    // API Keys endpoints
    API_KEYS: {
        CREATE: '/api/api-keys',
        LIST: '/api/api-keys',
        UPDATE: '/api/api-keys/:id',
        DELETE: '/api/api-keys/:id',
        GRANTABLE_SCOPES: '/api/api-keys/grantable-scopes',
    },
};
/**
 * Utility function to interpolate route parameters
 * @param route - The route template with :param placeholders
 * @param params - Object containing parameter values
 * @returns The route with parameters interpolated
 *
 * @example
 * interpolateRoute(API_ROUTES.AGENTS.GET_BY_ID, { id: '123' })
 * // Returns: '/api/agents/123'
 */
export function interpolateRoute(route, params) {
    return Object.entries(params).reduce((interpolatedRoute, [key, value]) => interpolatedRoute.replace(new RegExp(`:${key}`, 'g'), String(value)), route);
}
//# sourceMappingURL=api-routes.js.map